/**
 * Reading progress.
 *
 * localStorage is the source every screen reads from, so progress works for
 * signed-out readers and needs no request to render. For a signed-in reader it
 * is also a cache: writes are mirrored to the account, and signing in merges
 * the two so a place kept on a phone is there on a laptop.
 *
 * Every read and write is guarded — private windows, cleared site data and
 * storage-blocking browsers all throw, and none of that is worth a broken page.
 */
import http from '../api/http-client';
import { hasAccessToken } from '../features/auth/token-store';

const KEY = 'jslipi-reading-v1';
const MAX_ENTRIES = 60;
/** Below this we assume the reader bounced rather than started. */
const MIN_PERCENT_TO_REMEMBER = 3;
/** At or above this, a post counts as finished. */
export const FINISHED_PERCENT = 88;

export interface ReadingEntry {
  blogSlug: string;
  blogTitle: string;
  postSlug: string;
  postTitle: string;
  /** 0–100, furthest point reached. */
  percent: number;
  /** id of the heading the reader had reached, so resuming lands on it. */
  anchor?: string;
  /** epoch ms of the last time this post was open. */
  readAt: number;
}

function readAll(): ReadingEntry[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ReadingEntry[]) : [];
  } catch {
    return [];
  }
}

function writeAll(entries: ReadingEntry[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch {
    /* storage unavailable or full — progress is a convenience, not a promise */
  }
}

/**
 * Push to the account, at most once every few seconds per post.
 *
 * Failures are swallowed: the local copy already has it, and the next sign-in
 * merges anything that never made it.
 */
const PUSH_INTERVAL_MS = 5000;
const lastPush = new Map<string, number>();

function pushToAccount(entry: ReadingEntry): void {
  if (!hasAccessToken()) return;

  const key = `${entry.blogSlug}/${entry.postSlug}`;
  const now = Date.now();
  // Always send a finished read — that is the one the reader will look for on
  // another device, and losing it to a throttle would be the visible failure.
  if (entry.percent < FINISHED_PERCENT && now - (lastPush.get(key) ?? 0) < PUSH_INTERVAL_MS) return;

  lastPush.set(key, now);
  void http.put('/reading', entry).catch(() => undefined);
}

/** Record (or advance) progress for one post. Never moves a percentage backwards. */
export function recordProgress(entry: Omit<ReadingEntry, 'readAt'>): void {
  if (!entry.blogSlug || !entry.postSlug) return;
  if (entry.percent < MIN_PERCENT_TO_REMEMBER) return;

  const entries = readAll();
  const i = entries.findIndex(
    (e) => e.blogSlug === entry.blogSlug && e.postSlug === entry.postSlug
  );

  const prev = i >= 0 ? entries[i] : undefined;

  const next: ReadingEntry = {
    ...entry,
    // Percent is the furthest point reached, so it never moves backwards.
    percent: Math.round(Math.min(100, Math.max(entry.percent, prev?.percent ?? 0))),
    // The anchor is the opposite: it is where you stopped *this* time, which is
    // where resuming should return you — not your high-water mark from a
    // previous read. Keep the old one only when this visit found none.
    anchor: entry.anchor ?? prev?.anchor,
    readAt: Date.now(),
  };

  if (i >= 0) entries.splice(i, 1);
  entries.unshift(next);
  writeAll(entries);
  pushToAccount(next);
}

/**
 * Sign-in: fold the browser's progress into the account's and keep the union.
 *
 * Called after a session is restored or created. Reading signed-out on a new
 * device is exactly the case this exists for — the local entries would
 * otherwise be stranded, and replacing them with the account's would lose
 * them.
 */
export async function syncProgressWithAccount(): Promise<void> {
  if (!hasAccessToken()) return;

  try {
    const { data } = await http.post<{ progress: ReadingEntry[] }>('/reading/merge', {
      entries: readAll(),
    });

    if (Array.isArray(data?.progress)) writeAll(data.progress);
  } catch {
    /* offline, or the session expired — the local copy still stands */
  }
}

/** The most recently opened post that is not already finished. */
export function getContinueReading(): ReadingEntry | null {
  const unfinished = readAll().filter((e) => e.percent < FINISHED_PERCENT);
  return unfinished[0] ?? null;
}

export function getMostRecent(): ReadingEntry | null {
  return readAll()[0] ?? null;
}

/** What was stored for one post, if anything — used to restore the scroll position. */
export function getEntry(blogSlug: string, postSlug: string): ReadingEntry | null {
  return (
    readAll().find((e) => e.blogSlug === blogSlug && e.postSlug === postSlug) ?? null
  );
}

/** How far through a series the reader is, by counting finished posts. */
export function getSeriesProgress(blogSlug: string): { finished: number; started: number } {
  const entries = readAll().filter((e) => e.blogSlug === blogSlug);
  return {
    finished: entries.filter((e) => e.percent >= FINISHED_PERCENT).length,
    started: entries.length,
  };
}

/** Percent reached for every post of one series, keyed by post slug. */
export function getSeriesEntries(blogSlug: string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const entry of readAll()) {
    if (entry.blogSlug === blogSlug) out[entry.postSlug] = entry.percent;
  }
  return out;
}

export function getFinishedCount(): number {
  return readAll().filter((e) => e.percent >= FINISHED_PERCENT).length;
}

export function clearProgress(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}
